/**
 * Reading and writing alert rules, without any React in the way.
 *
 * Two jobs. Prose: a rule is a nested structure of conditions, filters and
 * actions, and a list of them is only useful if each one reads as a sentence.
 * Validation: `outpost_alerts::AlertRule::validate` rejects rules the worker
 * could not act on, and the editor has to say so *before* the request, because
 * a 422 on save cannot point at the field that caused it.
 *
 * The checks here therefore mirror that function — and go slightly further,
 * refusing rules the server would store but the worker would silently skip (an
 * email action with no recipients) or that could never match (a filter with no
 * value). Anything this module accepts, the server must accept too; the reverse
 * is deliberately not true.
 */

import type {
  AlertAction,
  AlertCondition,
  AlertFilter,
  AlertRule,
  AlertRuleInput,
  Integration,
  IntegrationKind,
  Level,
  MatchOp,
} from '@outpost/api-client';

export type ConditionType = AlertCondition['type'];
export type FilterType = AlertFilter['type'];
export type ActionType = AlertAction['type'];

export const CONDITION_TYPES: ConditionType[] = [
  'new_issue',
  'issue_regression',
  'event_frequency',
  'user_frequency',
  'every_event',
];

export const FILTER_TYPES: FilterType[] = [
  'level',
  'environment',
  'release',
  'tag',
  'issue_age_under',
];

export const ACTION_TYPES: ActionType[] = ['email', 'webhook', 'slack'];

export const LEVELS: Level[] = ['debug', 'info', 'warning', 'error', 'fatal'];

/**
 * Which comparisons each filter offers.
 *
 * `contains` is left out of `level` because the server treats it as equality —
 * offering it would promise a substring match that does not exist. And only
 * `level` offers `greater_or_equal`, which on a string compares lexically.
 */
export const FILTER_OPS: Record<'level' | 'environment' | 'release' | 'tag', MatchOp[]> = {
  level: ['greater_or_equal', 'equals', 'not_equals'],
  environment: ['equals', 'not_equals', 'contains'],
  release: ['equals', 'not_equals', 'contains'],
  tag: ['equals', 'not_equals', 'contains'],
};

/** The rule a fresh editor starts from: the one most people want first. */
export function emptyRule(): AlertRuleInput {
  return {
    name: '',
    is_active: true,
    action_match: 'all',
    conditions: [{ type: 'new_issue' }],
    filters: [],
    actions: [{ type: 'email', targets: [] }],
    frequency_minutes: 30,
  };
}

/** Strip the server-assigned fields, so an existing rule can be edited. */
export function toInput(rule: AlertRule): AlertRuleInput {
  return {
    name: rule.name,
    is_active: rule.is_active,
    action_match: rule.action_match,
    conditions: rule.conditions,
    filters: rule.filters,
    actions: rule.actions,
    frequency_minutes: rule.frequency_minutes,
  };
}

export function newCondition(type: ConditionType): AlertCondition {
  switch (type) {
    case 'event_frequency':
      return { type, value: 100, interval_minutes: 60 };
    case 'user_frequency':
      return { type, value: 10 };
    default:
      return { type };
  }
}

export function newFilter(type: FilterType): AlertFilter {
  switch (type) {
    case 'level':
      return { type, op: 'greater_or_equal', value: 'error' };
    case 'tag':
      return { type, key: '', op: 'equals', value: '' };
    case 'issue_age_under':
      return { type, minutes: 60 };
    default:
      return { type, op: 'equals', value: '' };
  }
}

export function newAction(type: ActionType): AlertAction {
  return type === 'email' ? { type, targets: [] } : { type, integration_id: '' };
}

/** The integration kind an action of this type needs. Email needs none. */
export function actionIntegrationKind(type: ActionType): IntegrationKind | null {
  return type === 'email' ? null : type;
}

// --- prose ------------------------------------------------------------------

export function describeCondition(condition: AlertCondition): string {
  switch (condition.type) {
    case 'new_issue':
      return 'a new issue appears';
    case 'issue_regression':
      return 'a resolved issue happens again';
    case 'event_frequency':
      return `an issue is seen more than ${condition.value} times in ${minutes(condition.interval_minutes)}`;
    case 'user_frequency':
      return `an issue affects more than ${condition.value} ${plural(condition.value, 'user')}`;
    case 'every_event':
      return 'any event is received';
    default:
      // A rule written by a newer server, or by hand through the API.
      return unknownType(condition);
  }
}

export function describeFilter(filter: AlertFilter): string {
  switch (filter.type) {
    case 'level':
      return filter.op === 'greater_or_equal'
        ? `the level is ${filter.value} or higher`
        : `the level ${comparison(filter.op)} ${filter.value}`;
    case 'environment':
      return `the environment ${comparison(filter.op)} ${filter.value}`;
    case 'release':
      return `the release ${comparison(filter.op)} ${filter.value}`;
    case 'tag':
      return `the tag ${filter.key} ${comparison(filter.op)} ${filter.value}`;
    case 'issue_age_under':
      return `the issue is less than ${minutes(filter.minutes)} old`;
    default:
      return unknownType(filter);
  }
}

/**
 * An action as a short label. Webhook and Slack name their integration, so
 * `integrations` is looked up rather than showing a UUID nobody recognises.
 * Pass `undefined` while that list is still loading: claiming the integration
 * is unknown before looking would be worse than not naming it.
 */
export function describeAction(action: AlertAction, integrations?: Integration[]): string {
  switch (action.type) {
    case 'email':
      return action.targets.length > 0 ? `Email ${action.targets.join(', ')}` : 'Email (nobody)';
    case 'webhook':
    case 'slack': {
      const label = action.type === 'slack' ? 'Slack' : 'Webhook';
      if (!integrations) return label;
      const named = integrations.find((integration) => integration.id === action.integration_id);
      // A deleted integration leaves a rule pointing at nothing; say so rather
      // than rendering an empty label.
      return `${label}: ${named?.name ?? 'unknown integration'}`;
    }
    default:
      return unknownType(action);
  }
}

const COMPARISONS: Record<MatchOp, string> = {
  equals: 'is',
  not_equals: 'is not',
  contains: 'contains',
  greater_or_equal: 'is at least',
};

export function comparison(op: MatchOp): string {
  return COMPARISONS[op] ?? String(op).replaceAll('_', ' ');
}

/** `90` → `1h 30m`, because "at most every 1440 minutes" is unreadable. */
export function minutes(total: number): string {
  if (!Number.isFinite(total) || total <= 0) return `${total} minutes`;
  const hours = Math.floor(total / 60);
  const rest = total % 60;
  if (hours === 0) return `${rest} ${plural(rest, 'minute')}`;
  if (rest === 0) return `${hours} ${plural(hours, 'hour')}`;
  return `${hours}h ${rest}m`;
}

function plural(count: number, word: string): string {
  return count === 1 ? word : `${word}s`;
}

function unknownType(value: { type: string }): string {
  return `${value.type.replaceAll('_', ' ')} (not understood by this dashboard)`;
}

// --- validation -------------------------------------------------------------

/**
 * Everything wrong with a rule, in the order the editor shows the fields.
 *
 * Returning all of them at once beats failing on the first: fixing one problem
 * only to be told about the next is how a form earns its reputation.
 */
export function validateRule(rule: AlertRuleInput, integrations: Integration[] = []): string[] {
  const problems: string[] = [];

  if (!rule.name.trim()) problems.push('The rule needs a name.');

  if (rule.conditions.length === 0) {
    problems.push('Add at least one condition, or the rule can never fire.');
  }
  for (const condition of rule.conditions) {
    if (condition.type === 'event_frequency') {
      if (!positive(condition.value)) {
        problems.push('The event count to alert above must be a whole number above zero.');
      }
      if (!positive(condition.interval_minutes)) {
        problems.push('The event frequency interval must be a whole number of minutes above zero.');
      }
    }
    if (condition.type === 'user_frequency' && !whole(condition.value, 0)) {
      problems.push('The number of users to alert above must be zero or more.');
    }
  }

  for (const filter of rule.filters) {
    if (filter.type === 'tag' && !filter.key.trim()) {
      problems.push('The tag filter needs a tag key.');
    }
    if ('value' in filter && typeof filter.value === 'string' && !filter.value.trim()) {
      problems.push(`The ${filter.type.replaceAll('_', ' ')} filter needs a value.`);
    }
    if (filter.type === 'issue_age_under' && !positive(filter.minutes)) {
      problems.push('The issue age must be a whole number of minutes above zero.');
    }
  }

  if (rule.actions.length === 0) {
    problems.push('Add at least one action, or firing the rule does nothing.');
  }
  for (const action of rule.actions) {
    if (action.type === 'email') {
      // The worker drops an email action with no targets, so a rule saved this
      // way would look armed and never notify anyone.
      if (action.targets.length === 0) problems.push('The email action needs a recipient.');
      const bad = action.targets.filter((target) => !isEmail(target));
      if (bad.length > 0) problems.push(`Not an email address: ${bad.join(', ')}.`);
    } else {
      const integration = integrations.find((candidate) => candidate.id === action.integration_id);
      if (!action.integration_id) {
        problems.push(`Choose which ${action.type} integration to notify.`);
      } else if (integrations.length > 0 && !integration) {
        problems.push('That integration no longer exists. Choose another.');
      } else if (integration && !integration.is_active) {
        problems.push(`The integration “${integration.name}” is disabled, so it cannot notify.`);
      }
    }
  }

  if (!positive(rule.frequency_minutes)) {
    problems.push('The minimum gap between notifications must be at least one minute.');
  }

  return problems;
}

/**
 * Loose on purpose: the server sends the address, and a regex that tries to
 * decide RFC 5322 rejects addresses that work. This only catches typing a name.
 */
function isEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@.]+\.[^\s@]+$/.test(value.trim());
}

function whole(value: number, min: number): boolean {
  return Number.isInteger(value) && value >= min;
}

function positive(value: number): boolean {
  return whole(value, 1);
}
